import {
  type GraphicImagePairWithData,
  type ImageDeliverable,
} from '@bizzdesign/modeler/models';
import { toImageClassificationFields } from '@bizzdesign/modeler/utils-canvazz';

const MAX_IMAGE_CACHE_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB

/**
 * Evicts the data of the oldest removable images until the cache is within its size limit.
 * @param images Current cached images.
 * @param evictableImageIds Image IDs marked for removal, ordered oldest to newest. An id may not
 *   be cached yet (e.g. its download is still in flight) — it remains a candidate until it is
 *   evicted after arriving or explicitly restored, so a late upsert cannot bypass eviction.
 * @returns The trimmed images and remaining eviction candidates.
 */
export function trimImageCache(
  images: ImageDeliverable[],
  evictableImageIds: string[],
): { images: ImageDeliverable[]; evictableImageIds: string[] } {
  const imagesById = new Map(images.map((image) => [image.id, image]));
  const evictedImageIds = new Set<string>();
  // imageSrc is always an ASCII "data:...;base64,..." URL, so .length (UTF-16 code units) already
  // equals its byte size — no separate byte-counting pass is needed.
  let cacheSize = images.reduce(
    (size, { imageSrc }) => size + (imageSrc?.length ?? 0),
    0,
  );

  for (const imageId of evictableImageIds) {
    if (cacheSize <= MAX_IMAGE_CACHE_SIZE_BYTES) break;
    const image = imagesById.get(imageId);
    if (!image) continue;
    cacheSize -= image.imageSrc?.length ?? 0;
    evictedImageIds.add(imageId);
  }

  return {
    images: images.map((image) =>
      evictedImageIds.has(image.id) ? { ...image, imageSrc: undefined } : image,
    ),
    evictableImageIds: evictableImageIds.filter(
      (id) => !evictedImageIds.has(id),
    ),
  };
}

export function toImageDeliverable(
  item: GraphicImagePairWithData,
): ImageDeliverable | undefined {
  const { imageId, imageSrc, description, alt } = item;
  if (imageSrc === undefined) return undefined;

  return {
    id: imageId,
    imageSrc,
    ...(description === undefined ? {} : { description }),
    ...(alt === undefined ? {} : { alt }),
    ...toImageClassificationFields(item),
  };
}

export function upsertImages(
  currentImages: ImageDeliverable[],
  nextImages: ImageDeliverable[],
): ImageDeliverable[] {
  const imageById = new Map(currentImages.map((image) => [image.id, image]));
  nextImages.forEach((image) => imageById.set(image.id, image));

  return [...imageById.values()];
}
